import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import api from '../utils/api';

interface Template {
  id: number;
  filename: string;
  display_name: string;
  filepath: string;
  size: number;
  sheet_names: string[];
  uploaded_at: string;
}

const TemplateManager: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await api.get('/template-manager/list');
      setTemplates(Array.isArray(data) ? data : []);
      setError(null);
    } catch (error: any) {
      setError('Ошибка загрузки списка шаблонов');
      console.error(error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (ext !== 'xlsx' && ext !== 'xls') {
        setError('Допустимы только файлы .xlsx и .xls');
        return;
      }
      setSelectedFile(file);
      setOpenDialog(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();
    formData.append('template', selectedFile);
    // Передаём правильное имя файла
    const cleanName = selectedFile.name.replace(/[^\w\sа-яА-ЯёЁ.-]/g, ' ').trim();
    formData.append('displayName', cleanName);

    try {
      const data = await api.upload(
        '/template-manager/upload',
        formData,
        (percent) => setUploadProgress(percent)
      );

      setSuccess(`Шаблон "${data.displayName || selectedFile.name}" успешно загружен!`);
      setOpenDialog(false);
      setSelectedFile(null);
      loadTemplates();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Ошибка загрузки шаблона');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownloadManagerTemplate = async () => {
    setLoading(true);
    try {
      const blob = await api.download('/template-manager/download-manager-template');
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Календарь_для_менеджеров_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setSuccess('Шаблон для менеджеров успешно скачан!');
    } catch (error: any) {
      setError('Ошибка скачивания шаблона');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, filename: string) => {
    if (!window.confirm(`Удалить шаблон "${filename}"?`)) return;

    try {
      await api.delete(`/template-manager/${id}`);
      setSuccess(`Шаблон "${filename}" удалён`);
      loadTemplates();
    } catch (error: any) {
      setError('Ошибка удаления шаблона');
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSize = (bytes: number) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  // Функция для декодирования имени файла
  const decodeFilename = (filename: string): string => {
    if (!filename) return '';
    try {
      // Пробуем декодировать URL-кодировку
      let decoded = filename;
      if (filename.includes('%')) {
        decoded = decodeURIComponent(filename);
      }
      // Убираем timestamp и дефис в начале
      decoded = decoded.replace(/^\d+-/, '');
      // Убираем расширение для отображения
      return decoded.replace(/\.(xlsx|xls)$/, '');
    } catch (e) {
      return filename;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Управление шаблонами календаря
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadIcon />}
            disabled={uploading}
          >
            Загрузить макет
            <input
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
            />
          </Button>

          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadManagerTemplate}
            disabled={loading}
          >
            Скачать шаблон для менеджеров
          </Button>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadTemplates}
            disabled={loading}
          >
            Обновить
          </Button>
        </Box>

        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          * Для скачивания шаблона для менеджеров необходим загруженный макет
        </Typography>
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Подтверждение загрузки</DialogTitle>
        <DialogContent>
          <Typography>
            Загрузить макет: <strong>{selectedFile?.name}</strong>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Размер: {selectedFile ? formatSize(selectedFile.size) : ''}
          </Typography>
          {uploading && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption">
                {uploadProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={uploading}>
            Отмена
          </Button>
          <Button 
            onClick={handleUpload} 
            variant="contained" 
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? 'Загрузка...' : 'Загрузить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
        Загруженные макеты
      </Typography>

      {loading && templates.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          <Typography color="textSecondary">
            Нет загруженных макетов
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Загрузите макет для создания шаблонов с выпадающими списками
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <List>
            {templates.map((template, index) => {
              // Получаем отображаемое имя
              const displayName = template.display_name || decodeFilename(template.filename);
              
              return (
                <React.Fragment key={template.id || index}>
                  <ListItem
                    secondaryAction={
                      <IconButton 
                        edge="end" 
                        onClick={() => handleDelete(template.id, displayName)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={displayName}
                      secondary={
                        <>
                          <span>Файл: {template.filename}</span>
                          <br />
                          <span>Загружен: {formatDate(template.uploaded_at)}</span>
                          {template.size && (
                            <span> • Размер: {formatSize(template.size)}</span>
                          )}
                          {template.sheet_names && Array.isArray(template.sheet_names) && (
                            <span> • Листы: {template.sheet_names.join(', ')}</span>
                          )}
                        </>
                      }
                    />
                    <Chip 
                      label={template.filename?.endsWith('.xlsx') ? 'XLSX' : 'XLS'} 
                      size="small" 
                      color="primary" 
                      sx={{ mr: 2 }}
                    />
                  </ListItem>
                  {index < templates.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default TemplateManager;
